export const weekDays = [
    "Hétfő",
    "Kedd",
    "Szerda",
    "Csütörtök",
    "Péntek",
    "Szombat",
    "Vasárnap",
];

export const weekDaysEn = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export function isEven(n: number): boolean {
    return n % 2 === 0;
}

export function formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function getDateFromWeek(
    year: number,
    week: number,
    day: number
): string {
    // Create a date representing Jan 4th (guaranteed to be in the first ISO week)
    const jan4 = new Date(year, 0, 4);

    // Find the Monday of the first ISO week
    const dayOfWeek = jan4.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const isoWeekStart = new Date(jan4);
    const diff = (dayOfWeek + 6) % 7; // Convert Sunday=0 to Sunday=6, Monday=1 to Monday=0, etc.
    isoWeekStart.setDate(jan4.getDate() - diff);

    // Add the number of weeks and the day offset
    const targetDate = new Date(isoWeekStart);
    targetDate.setDate(isoWeekStart.getDate() + (week - 1) * 7 + day);

    return formatDateToYMD(targetDate);
}

export function filterObjectByKey(inputObj: any, minKey: any) {
    // Convert the input object keys into numbers and filter them
    let filteredObj = Object.keys(inputObj)
        .filter((key) => Number(key) >= minKey) // Keep keys >= minKey
        .reduce((acc: any, key) => {
            acc[key] = inputObj[key]; // Add the matching key-value pairs to a new object
            return acc;
        }, {});

    return filteredObj;
}

export function arraysEqual(a: number[], b: number[]): boolean {
    const setA = new Set(a);
    const setB = new Set(b);

    if (setA.size !== setB.size) return false;

    for (const item of setA) {
        if (!setB.has(item)) return false;
    }

    return true;
}

export const getEvenOdd = (week: number): "odd" | "even" =>
    week % 2 === 0 ? "even" : "odd";

export function getISOWeekNumber(date: Date): number {
    const target = new Date(date);
    target.setDate(target.getDate() - ((target.getDay() + 6) % 7) + 3); // Move to the nearest Thursday
    const firstThursday = new Date(target.getFullYear(), 0, 4);
    const diff = target.getTime() - firstThursday.getTime();
    const weekNumber = 1 + Math.round(diff / (7 * 86400000));
    return weekNumber;
}

export const getCurrentWeek = function (date = new Date()) {
    return getISOWeekNumber(date);
};

export function has53Weeks(year: number) {
    let firstDay = new Date(year, 0, 1).getDay(); // Get first day of the year (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    let isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    return firstDay === 4 || (isLeapYear && firstDay === 3); // Thursday (4) or Wednesday (3) in a leap year
}

export function isEmptyObject(obj: object): boolean {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function hasKey<T extends object>(obj: T, key: string): boolean {
    return key in obj;
}

export const filterWeekSettingsData = (data: any) => {
    const currentWeek = getCurrentWeek();
    const currentYear = new Date().getFullYear();
    let _weekNumbers = 52;

    if (has53Weeks(currentYear)) {
        _weekNumbers = 53;
    }

    // Filter individualWeekSettings
    let filteredData: any = {};

    // Handle current year
    if (data[currentYear]) {
        filteredData[currentYear] = {};
        Object.keys(data[currentYear]).forEach((week) => {
            if (parseInt(week) >= currentWeek) {
                filteredData[currentYear][week] = data[currentYear][week];
            }
        });
    }

    // Handle next year
    const nextYear = currentYear + 1;
    if (data[nextYear]) {
        filteredData[nextYear] = {};
        // Calculate how many weeks we need from next year
        const weeksFromCurrentYear = _weekNumbers - currentWeek + 1; // +1 because we include the current week
        const remainingWeeks = _weekNumbers - weeksFromCurrentYear;

        // Only include weeks up to the remaining weeks needed
        Object.keys(data[nextYear]).forEach((week) => {
            const weekNum = parseInt(week);
            if (weekNum <= remainingWeeks) {
                filteredData[nextYear][week] = data[nextYear][week];
            }
        });

        // Remove any weeks that are beyond the remaining weeks
        Object.keys(filteredData[nextYear]).forEach((week) => {
            const weekNum = parseInt(week);
            if (weekNum > remainingWeeks) {
                delete filteredData[nextYear][week];
            }
        });
    }

    return filteredData;
};

export const cleanRegularWeekSettings = (data: any) => {
    const { regularWeekSettings, individualWeekSettings } = data;

    // If no regular settings, return data as is
    if (
        !regularWeekSettings ||
        (!regularWeekSettings.oddWeekUsed &&
            Object.keys(regularWeekSettings.even || {}).length === 0 &&
            Object.keys(regularWeekSettings.odd || {}).length === 0)
    ) {
        return data;
    }

    // Get all days that have settings in individualWeekSettings
    const daysWithSettings = new Set<number>();

    // Check all years and weeks in individualWeekSettings
    Object.values(individualWeekSettings).forEach((yearData: any) => {
        Object.values(yearData).forEach((weekData: any) => {
            Object.keys(weekData).forEach((day) => {
                if (Object.keys(weekData[day]).length > 0) {
                    daysWithSettings.add(parseInt(day));
                }
            });
        });
    });

    // Create new objects for even and odd weeks
    const newEven: any = {};
    const newOdd: any = {};

    // Copy only the days that have settings
    if (regularWeekSettings.even) {
        Object.keys(regularWeekSettings.even).forEach((day) => {
            if (daysWithSettings.has(parseInt(day))) {
                newEven[day] = regularWeekSettings.even[day];
            }
        });
    }

    if (regularWeekSettings.odd) {
        Object.keys(regularWeekSettings.odd).forEach((day) => {
            if (daysWithSettings.has(parseInt(day))) {
                newOdd[day] = regularWeekSettings.odd[day];
            }
        });
    }

    // Create new regularWeekSettings object
    const cleanedRegularSettings = {
        ...regularWeekSettings,
        even: newEven,
        odd: newOdd,
        oddWeekUsed:
            Object.keys(newEven).length > 0 || Object.keys(newOdd).length > 0
                ? regularWeekSettings.oddWeekUsed
                : false,
    };

    return {
        ...data,
        regularWeekSettings: cleanedRegularSettings,
    };
};
